<template lang="pug">
    form.create-georadar-point(v-show="action")
        header.modal-header
            SectionHeader(:title="formTitle")
            div.subheader
                div.subheader-info Fill form below or click right on the map.
            
        div.modal-body
            div.form-subsection(v-if="globalError")
                el-alert(title="error" type="error" :closable="false" show-icon)
                    p(v-for="(message, index) in point.errors.message" :key="index") {{ message }}

            //- Label
            div.form-subsection
                div.form-label__required
                    label Label
                input.form-input(
                    id="label"
                    name="label"
                    placeholder="Position name"
                    v-model="label"
                    v-validate="'max:200|min:1|ascii|required'"
                    type="text"
                )
                span.form-validate-error {{ errors.first('label') }}
            //- Latitude
            div.form-subsection
                div.form-label__required
                    label Latitude
                input.form-input(
                    id="latitude"
                    name="latitude"
                    placeholder="48"
                    v-model="latitude"
                    v-validate="'decimal|min_value:-90|max_value:90|required'"
                    type="text"
                )
                span.form-validate-error {{ errors.first('latitude') }}
            //- Longitude
            div.form-subsection
                div.form-label__required
                    label Longitude
                input.form-input(
                    id="longitude"
                    name="longitude"
                    placeholder="4.995"
                    v-model="longitude"
                    v-validate="'decimal|min_value:-180|max_value:180|required'"
                    type="text"
                )
                span.form-validate-error {{ errors.first('longitude') }}
            //- Area
            div.form-subsection
                div.form-label__required
                    label Area
                input.form-input(
                    id="area"
                    name="area"
                    v-model="radius"
                    v-validate="'numeric|min_value:1|max_value:100|required'"
                    type="text"
                )
                span.form-validate-error {{ errors.first('area') }}
        //- Footer
        footer.modal-footer
            div.footer-content.l-flex-row--between
                button.btn.btn--medium.btn--round(@click="handleCancel" type="button") Cancel
                button.btn.btn--medium.btn--round-big.btn-rev(
                    type="button"
                    :class="{'ui-disabled': disabledSaveButton}"
                    @click="handleSubmit") {{ actionString }}
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { EMPTY_POINT, generateLinkBetweenFormAndStore } from '@/store/ui/georadar/helpers'
import { truncateCoordinate } from '@/store/data/georadar/helpers'

import SectionHeader from '@/components/common/SectionHeader'

export default {
    name: 'GeoradarForm',

    components: { SectionHeader },

    data() {
        return {
            globalError: false
        }
    },

    computed: {
        ...mapState('ui/georadar', {
            action: state => state.form.action,
            point: state => state.form.point,
            index: state => state.form.index
        }),

        ...mapState('data/georadar', {
            unSavedPoints: state => state.unSavedPoints
        }),

        ...generateLinkBetweenFormAndStore(Object.keys(EMPTY_POINT)),

        actionString() {
            const mapper = {
                create: 'add',
                update: 'edit'
            }
            return mapper[this.action]
        },

        formTitle() {
            return `${this.actionString} position`
        },

        allFieldAreDefined() {
            const fields = ['label', 'longitude', 'latitude', 'radius']
            return fields.every(field => this.point[field] != null && this.point[field] !== '')
        },

        pointIsNotUpdated() {
            if (this.action != 'update') return false

            const beforeUpdatePoint = this.unSavedPoints[this.index]
            return (
                this.point.label == beforeUpdatePoint.label &&
                this.point.longitude == beforeUpdatePoint.longitude &&
                this.point.latitude == beforeUpdatePoint.latitude &&
                this.point.radius == beforeUpdatePoint.radius
            )
        },

        disabledSaveButton() {
            return this.errors.items.length > 0 || !this.allFieldAreDefined || this.globalError || this.pointIsNotUpdated
        }
    },

    watch: {
        action(val) {
            if (val) {
                if (val === 'create') {
                    // After 2 ticks clear errors from VeeValidate
                    this.$nextTick(() => {
                        this.$nextTick(() => {
                            this.errors.clear()
                        })
                    })
                }
                if (val === 'update') {
                    this.$nextTick(() => {
                        this.setGlobalError(this.point)
                    })
                }
            }
        },
        point: {
            handler() {
                this.globalError = false
            },
            deep: true
        },
        latitude(val) {
            if (val) this.latitude = truncateCoordinate(val)
        },
        longitude(val) {
            if (val) this.longitude = truncateCoordinate(val)
        }
    },

    methods: {
        ...mapActions('data/georadar', ['addNewPoint', 'updateGeoradarPoint']),
        ...mapMutations('ui/georadar', ['setActiveTab', 'setFocusedPoint', 'setFormAction', 'setFormField', 'setFormUpdateInfos']),

        handleSubmit() {
            if (this.action === 'create') {
                this.add()
            }
            if (this.action === 'update') {
                this.update()
            }
        },

        handleCancel() {
            this.setFormAction(null)
            this.setFocusedPoint({})
            this.setFormUpdateInfos({ index: null })
        },

        async add() {
            try {
                const point = { ...this.point, action: 'create' }
                await this.addNewPoint({ point })
                this.setFormAction(null)
                this.setFormUpdateInfos({ index: null })
                this.$nextTick(() => {
                    this.setFocusedPoint({ index: this.unSavedPoints.length - 1 })
                })
            } catch (err) {
                throw err
            }
        },

        async update() {
            const index = this.index
            const point = { ...this.point }
            point.action = point.action || 'update'
            delete point.errors
            try {
                await this.updateGeoradarPoint({ index, point })
                this.setFormAction(null)
                this.setFormUpdateInfos({ index: null })
                this.setFocusedPoint({ index, shoudlScroll: false })
            } catch (err) {
                throw err
            }
        },

        setGlobalError(point) {
            if (point.errors && point.errors.message) {
                this.globalError = true
            }
        }
    }
}
</script>
